import { isDocEmpty } from "@smartsuite/smartdoc";
import type { EditorData } from "@smartsuite/types";
import cx from "clsx";
import DOMPurify from "dompurify";
import { memo, useMemo } from "react";

import { linkMentions, useFormAccountId } from "../../../helpers/publicMentions";

interface SmartDocRendererProps {
  className?: string;
  editorData: EditorData;
}

const SmartDocRendererRaw = ({
  className,
  editorData,
}: SmartDocRendererProps): JSX.Element | null => {
  const classes = cx(className, "editor-display ProseMirror ProseMirror--display");
  const accountId = useFormAccountId();

  // The HTML is authored by workspace users in the form builder — treat it as
  // untrusted and sanitize before injecting (stored-XSS defense in depth).
  const sanitizedHtml = useMemo(
    () =>
      DOMPurify.sanitize(linkMentions(editorData.html ?? "", accountId), {
        USE_PROFILES: { html: true },
        ADD_ATTR: ["target"],
        FORBID_TAGS: ["style", "form", "input", "button"],
      }),
    [editorData.html, accountId],
  );

  if (!editorData.html || isDocEmpty(editorData.data as Parameters<typeof isDocEmpty>[0])) {
    return null;
  }

  return <div className={classes} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

export const SmartDocRenderer = memo(SmartDocRendererRaw);
